import React, { useState, useEffect, useCallback, ReactNode } from 'react'
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react'
import clsx from 'clsx'

type PropType = {
  className?: string
  sliderClassName?: string
  options?: EmblaOptionsType
  autoScroll?: boolean
  slides: ReactNode[]
  navigationType?: 'basic' | 'arrow'
  buttonConfig?: {
    prevButtonClassName?: string
    nextButtonClassName?: string
  }
  indicatorSize?: 'sm' | 'lg'
}

let interval: NodeJS.Timeout
const AUTO_SCROLL_TIMER = 5000 // in ms

// TODO: pause carousel on visibility change = hidden
const Carousel = (props: PropType): JSX.Element => {
  const { options, slides, buttonConfig } = props
  const [carouselRef, carousel] = useEmblaCarousel({
    draggable: true,
    loop: true,
    align: 'center',
    inViewThreshold: 0,
    ...options
  })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const scrollPrev = useCallback(
    () => carousel && carousel.scrollPrev(),
    [carousel]
  )
  const scrollNext = useCallback(
    () => carousel && carousel.scrollNext(),
    [carousel]
  )
  const scrollTo = useCallback(
    (index: number) => carousel && carousel.scrollTo(index),
    [carousel]
  )

  const onSelect = useCallback(() => {
    if (!carousel) return
    setSelectedIndex(carousel.selectedScrollSnap())
  }, [carousel, setSelectedIndex])

  useEffect(() => {
    if (!carousel) return
    carousel.on('select', onSelect)
  }, [carousel, onSelect])

  useEffect(() => {
    if (!props.autoScroll || !carousel) return

    interval = setInterval(() => carousel.scrollNext(), AUTO_SCROLL_TIMER)
    return () => clearInterval(interval)
  }, [carousel, props.autoScroll])

  return (
    <div className={clsx('relative', props.className)} ref={carouselRef}>
      <div className="carousel__list  |  flex select-none">
        {slides.map((slide, index) => (
          <div
            className={clsx('relative min-w-full', props.sliderClassName)}
            key={index}
          >
            {slide}
          </div>
        ))}
      </div>
      {props.navigationType === 'arrow' ? (
        <>
          <button
            className={clsx(
              buttonConfig?.prevButtonClassName,
              'carousel__button'
            )}
            onClick={() => scrollPrev()}
            type="button"
          >
            <svg className="w-full h-full" viewBox="137.718 -1.001 366.563 644">
              <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
            </svg>
          </button>
          <button
            className={clsx(
              buttonConfig?.nextButtonClassName,
              'carousel__button'
            )}
            onClick={() => scrollNext()}
          >
            <svg className="w-full h-full" viewBox="0 0 238.003 238.003">
              <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
            </svg>
          </button>
        </>
      ) : (
        <div className="carousel__buttons |  flex list-none justify-center mt-6">
          {slides.map((_, index) => (
            <button
              key={index}
              className={clsx(
                'mx-2 rounded-full bg-gray-30 transition-colors',
                index === selectedIndex ? '!bg-blue-40' : '',
                props?.indicatorSize === 'sm'
                  ? 'w-2 h-2 md:w-3 md:h-3'
                  : 'w-4 h-4'
              )}
              type="button"
              onClick={() => scrollTo(index)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Carousel
